import { Configuration, FrontendApi, Session } from '@ory/client';
import axios from 'axios';
import decode from 'jwt-decode';
import { DateTime } from 'luxon';
import useConfigStore from '../store/config';
import useUserStore, { PERSISTED_AUTH_STORE_KEY } from '../store/user';
import { IS_MOCK_AUTH_MODE } from './mock';
import { mintToken } from './profileApi';

let ory: FrontendApi | null = null;

const getOry = (): FrontendApi => {
  if (!ory) {
    ory = new FrontendApi(
      new Configuration({
        basePath: useConfigStore.getState().allauth.kratosUrl,
        baseOptions: { withCredentials: true },
      }),
    );
  }
  return ory;
};

const kratosErrorId = (err: unknown): string | undefined => {
  if (!axios.isAxiosError(err)) {
    return undefined;
  }
  return (err.response?.data as { error?: { id?: string } } | undefined)?.error
    ?.id;
};

// Set when toSession() failed with `session_aal2_required` (MFA-enrolled
// user holding an aal1 session) — the next login flow must escalate to aal2.
let aal2Required = false;

/**
 * Returns the Kratos session, or null when the user needs to (re)login.
 * 403 `session_aal2_required` also requires a new login flow.
 * Other errors propagate.
 */
export const getSession = async (): Promise<Session | null> => {
  try {
    const { data } = await getOry().toSession();
    aal2Required = false;
    const expiresAt = data.expires_at
      ? DateTime.fromISO(data.expires_at)
      : undefined;
    if (expiresAt && expiresAt <= DateTime.now().plus({ seconds: 30 })) {
      return null;
    }
    return data;
  } catch (err) {
    const status = axios.isAxiosError(err) ? err.response?.status : undefined;
    if (status === 401) {
      aal2Required = false;
      return null;
    }
    if (status === 403 && kratosErrorId(err) === 'session_aal2_required') {
      aal2Required = true;
      return null;
    }
    throw err;
  }
};

interface KratosTraits {
  name?: { first?: string; last?: string };
}

/** The token carries no display name, but the Kratos identity does. */
export const sessionDisplayName = (session: Session): string => {
  const traits = session.identity?.traits as KratosTraits | undefined;
  return [traits?.name?.first, traits?.name?.last]
    .filter((part) => !!part)
    .join(' ');
};

const createLoginFlow = async (): Promise<{ request_url: string }> => {
  // Decided (Q4): always land on the app root after login — and never pass
  // anything but our own origin, so a login URL can't loop back to itself.
  const returnTo = window.location.origin;
  const ory = getOry();
  if (aal2Required) {
    // aal1 session on an MFA-enrolled identity — prompt the second factor.
    const { data } = await ory.createBrowserLoginFlow({
      returnTo,
      aal: 'aal2',
    });
    return data;
  }
  try {
    const { data } = await ory.createBrowserLoginFlow({ returnTo });
    return data;
  } catch (err) {
    // Kratos still considers the session alive (e.g. it expires within our
    // 30s buffer) and rejects a plain flow — re-authenticate instead.
    // `aal: 'aal1'` re-verifies only the password; Kratos never downgrades
    // the session's AAL on refresh (cf. trafikportalen-ui login.tsx).
    if (kratosErrorId(err) === 'session_already_available') {
      const { data } = await ory.createBrowserLoginFlow({
        returnTo,
        refresh: true,
        aal: 'aal1',
      });
      return data;
    }
    throw err;
  }
};

let loginRedirectStarted = false;

// Module-level dedupe so a stampede of parallel API calls mints one token.
let pendingTokenRequest: Promise<string> | null = null;

const clearPendingTokenRequest = (): void => {
  pendingTokenRequest = null;
};

export const switchProfile = (): void => {
  if (IS_MOCK_AUTH_MODE) {
    return;
  }
  clearPendingTokenRequest();
  useUserStore.getState().clearProfile({ suppressAutoSelect: true });
};

const profileIdFromStoredAuth = (value: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(value) as {
      state?: { userProfileId?: string | null };
    };
    return parsed.state?.userProfileId || undefined;
  } catch {
    return undefined;
  }
};

// Registered once at boot (see index.tsx) rather than at import time, so the
// module has no global side effects and tests can attach/detach explicitly.
export const registerAuthListeners = (): (() => void) => {
  // A successful redirect leaves loginRedirectStarted set. When the user
  // presses Back on the Kratos page the document is restored from the
  // back/forward cache with the JS heap intact — reload so boot state is
  // derived from Kratos again.
  const onPageShow = (event: PageTransitionEvent): void => {
    if (event.persisted && loginRedirectStarted) {
      window.location.reload();
    }
  };

  // Keep sibling tabs in sync when the persisted auth store changes elsewhere
  // (a profile switch or logout in another tab).
  const onStorage = (event: StorageEvent): void => {
    if (
      event.storageArea !== window.localStorage ||
      event.key !== PERSISTED_AUTH_STORE_KEY
    ) {
      return;
    }
    clearPendingTokenRequest();
    const currentProfileId = useUserStore.getState().userProfileId;
    const previousProfileId =
      profileIdFromStoredAuth(event.oldValue) ?? currentProfileId;
    const nextProfileId = profileIdFromStoredAuth(event.newValue);
    if (previousProfileId !== nextProfileId) {
      // A different profile (or logout) elsewhere — reboot from stored state.
      window.location.reload();
      return;
    }
    void useUserStore.persist.rehydrate();
  };

  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener('pageshow', onPageShow);
    window.removeEventListener('storage', onStorage);
  };
};

export const startLogin = async (): Promise<void> => {
  if (loginRedirectStarted) {
    return;
  }
  loginRedirectStarted = true;
  try {
    const flow = await createLoginFlow();
    window.location.href = flow.request_url;
  } catch (err) {
    loginRedirectStarted = false;
    throw err;
  }
};

export const logout = async (): Promise<void> => {
  try {
    const { data } = await getOry().createBrowserLogoutFlow({
      returnTo: window.location.origin,
    });
    useUserStore.getState().clearProfile({ suppressAutoSelect: true });
    clearPendingTokenRequest();
    window.location.replace(data.logout_url);
  } catch (err) {
    if (!axios.isAxiosError(err) || err.response?.status !== 401) {
      throw err;
    }
    // Kratos confirms there is no server session left to destroy, so clearing
    // the local profile completes the logout.
    useUserStore.getState().clearProfile({ suppressAutoSelect: true });
    clearPendingTokenRequest();
    window.location.replace(window.location.origin);
  }
};

export const isAccessTokenValid = (token: string | null): boolean => {
  if (!token) {
    return false;
  }
  try {
    const { exp } = decode<{ exp: number }>(token);
    return exp > DateTime.now().toSeconds() + 30;
  } catch {
    return false;
  }
};

/**
 * Mint-failure matrix (see AUTH_MIGRATION.md §4.2): it is NOT simply
 * "401 = session died".
 *  - 401: no/expired Kratos session → full re-login.
 *  - 400/403/404: profile deleted/expired/deactivated or no longer ours →
 *    clear the persisted profile and fall back to the profile selector.
 *  - network errors: leave state; the caller surfaces the failure.
 */
const handleMintFailure = (err: unknown): void => {
  if (!axios.isAxiosError(err) || !err.response) {
    return;
  }
  const { status } = err.response;
  if (status === 401) {
    // The Kratos session is gone, so any aal2 requirement latched from an
    // earlier whoami is stale — a fresh login flow applies Kratos's normal
    // policy (including MFA) by itself.
    aal2Required = false;
    startLogin().catch(console.error);
    return;
  }
  if (status === 400 || status === 403 || status === 404) {
    // Suppress auto-select so the selector doesn't immediately re-pick
    // (and re-fail) the same profile.
    useUserStore.getState().clearProfile({ suppressAutoSelect: true });
    clearPendingTokenRequest();
  }
};

/**
 * The Kratos session cookie is the long-lived credential; the JWT is a
 * 10-minute derivative renewed lazily. Both API clients call this per
 * request, so a fresh-enough token is guaranteed on every call.
 */
export const getValidToken = async (): Promise<string> => {
  const { accessToken, userProfileId } = useUserStore.getState();
  if (accessToken && isAccessTokenValid(accessToken)) {
    return accessToken;
  }
  if (!userProfileId) {
    return '';
  }
  if (pendingTokenRequest) {
    return pendingTokenRequest;
  }
  const request = mintToken(userProfileId)
    .then((token) => {
      const store = useUserStore.getState();
      // A profile switch may have happened while the mint was in flight.
      if (store.userProfileId === userProfileId) {
        store.setAccessToken(token);
      }
      return token;
    })
    .catch((err) => {
      // A profile switch may have happened while the mint was in flight —
      // never let a stale failure clear the newly selected profile or
      // trigger a spurious login redirect.
      if (useUserStore.getState().userProfileId === userProfileId) {
        handleMintFailure(err);
      }
      throw err;
    })
    .finally(() => {
      if (pendingTokenRequest === request) {
        pendingTokenRequest = null;
      }
    });
  pendingTokenRequest = request;
  return request;
};

let unauthorizedRemintAt = 0;

/**
 * A 401 from mobilix-api with a time-valid JWT means the profile was
 * deactivated mid-session (auth-api refuses to re-mint, but issued JWTs stay
 * valid until exp). Drop the cached token so the next call re-mints; a dead
 * session or profile then surfaces through the mint-failure matrix (login
 * redirect / profile selector). The 60s window keeps straggler requests that
 * still carried the pre-renewal token from re-dropping a fresh one. Called
 * from the global react-query error handlers.
 */
export const handleApiUnauthorized = (): void => {
  const store = useUserStore.getState();
  if (!store.userProfileId || !store.accessToken) {
    // Not logged in, or a re-mint is already underway.
    return;
  }
  const now = DateTime.now().toMillis();
  if (now - unauthorizedRemintAt < 60_000) {
    return;
  }
  unauthorizedRemintAt = now;
  store.setAccessToken(null);
};
